import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase, supabasePasswordReset } from '../lib/supabaseClient';
import { clearLocalSession, clearPersistedAuthArtifacts, loginWithPassword, logout, requestPasswordReset } from '../lib/authFlow';
import { buildPermissionSet, clearAuthorizationCache, fetchMyAuthorization, hasPermission as hasPermissionImpl, isAdminRole } from '../lib/authorization';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [authzLoading, setAuthzLoading] = useState(true); // Começa como true para evitar race condition
  const [profile, setProfile] = useState(null);
  const [profileRole, setProfileRole] = useState(null);
  const [profileStatus, setProfileStatus] = useState(null);
  const [permissionSet, setPermissionSet] = useState({ allow: new Set(), deny: new Set() });
  const [isImpersonatingStrict, setIsImpersonatingStrict] = useState(() => {
    try { return window.sessionStorage.getItem('sofera:impersonation:strict') === '1'; } catch { return false; }
  });

  useEffect(() => {
    const syncImpersonationStrict = () => {
      try { setIsImpersonatingStrict(window.sessionStorage.getItem('sofera:impersonation:strict') === '1'); } catch { setIsImpersonatingStrict(false); }
    };
    syncImpersonationStrict();
    window.addEventListener('storage', syncImpersonationStrict);
    window.addEventListener('focus', syncImpersonationStrict);
    return () => {
      window.removeEventListener('storage', syncImpersonationStrict);
      window.removeEventListener('focus', syncImpersonationStrict);
    };
  }, []);

  useEffect(() => {
    try { setIsImpersonatingStrict(window.sessionStorage.getItem('sofera:impersonation:strict') === '1'); } catch { setIsImpersonatingStrict(false); }
  }, [session?.user?.id, session?.access_token]);

  useEffect(() => {
    let mounted = true;
    const resetAuthState = async () => {
      clearAuthorizationCache();
      clearPersistedAuthArtifacts();
      try { await clearLocalSession(supabase); } catch {}
      if (!mounted) return;
      setProfile(null);
      setProfileRole(null);
      setProfileStatus(null);
      setPermissionSet({ allow: new Set(), deny: new Set() });
      setSession(null);
    };

    const boot = async () => {
      const restore = async () => {
        const { data } = await supabase.auth.getSession();
        const next = data?.session || null;
        if (next && (!next.access_token || !next.refresh_token)) {
          console.warn('[AuthContext] sessão inválida detectada no boot (sem access/refresh token)');
          await resetAuthState();
          return;
        }
        if (next?.access_token) {
          const { data: userData, error: userErr } = await supabase.auth.getUser();
          if (userErr || !userData?.user?.id) {
            console.warn('[AuthContext] sessão inválida detectada no boot (getUser falhou)', userErr);
            await resetAuthState();
            return;
          }
        }
        if (!mounted) return;
        setSession(next);
      };

      try {
        await restore();
      } catch (err) {
        // Falha transitória ao ler a sessão (ex.: lock do GoTrue). NUNCA tratar
        // como "sem sessão": sem o retry o usuário cai na tela de login mesmo
        // estando autenticado e o link do curso é perdido.
        console.warn('[AuthContext] falha ao restaurar sessão no boot, tentando novamente', err);
        try {
          await restore();
        } catch (retryErr) {
          console.warn('[AuthContext] não foi possível restaurar a sessão no boot', retryErr);
        }
      } finally {
        if (mounted) setBootLoading(false);
      }
    };
    boot();
    const { data: sub } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === 'TOKEN_REFRESH_FAILED') {
        void supabase.auth.getSession()
          .then(async ({ data }) => {
            const current = data?.session || null;
            if (current?.access_token && current?.refresh_token) {
              const { data: userData, error: userErr } = await supabase.auth.getUser();
              if (!userErr && userData?.user?.id) {
                setSession(current);
                return;
              }
            }
            await resetAuthState();
          })
          .catch(async () => {
            await resetAuthState();
          });
          return;
        }

      const next = nextSession || null;
      if (next && (!next.access_token || !next.refresh_token)) {
        console.warn('[AuthContext] sessão inválida detectada no onAuthStateChange (sem access/refresh token)');
        void resetAuthState();
        return;
      }
      setSession(next);
    });
    return () => {
      mounted = false;
      try { sub?.subscription?.unsubscribe?.(); } catch {}
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!session?.user?.id) {
        clearAuthorizationCache();
        if (!mounted) return;
        setProfile(null);
        setProfileRole(null);
        setProfileStatus(null);
        setPermissionSet({ allow: new Set(), deny: new Set() });
        setAuthzLoading(false);
        return;
      }
      setAuthzLoading(true);
      try {
        const res = await fetchMyAuthorization(supabase);
        let normalizedStatus = res.status || null;

        // Fallback robusto: no login real, garante ativação imediata quando ainda vier aguardando convite/ativação.
        if (
          !isImpersonatingStrict
          && ['pendente', 'programado'].includes(String(normalizedStatus || '').toLowerCase())
          && session?.user?.id
        ) {
          try {
            const { error: activateErr } = await supabase
              .from('profiles')
              .update({ status: 'Ativo' })
              .eq('id', session.user.id)
              .in('status', ['Pendente', 'Programado']);
            if (!activateErr) normalizedStatus = 'Ativo';
          } catch {}
        }

        if (!mounted) return;
        setProfile(res.profile || null);
        setProfileRole(res.role || null);
        setProfileStatus(normalizedStatus);
        setPermissionSet(buildPermissionSet({ role: res.role, permissionsJson: res.permissionsJson }));
      } catch {
        if (!mounted) return;
        setProfile(null);
        setProfileRole(null);
        setProfileStatus(null);
        setPermissionSet({ allow: new Set(), deny: new Set() });
      } finally {
        if (mounted) setAuthzLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [session?.user?.id, session?.access_token]);

  const isAuthenticated = !!session;
  const legacyRole = (() => {
    try {
      const u = session?.user;
      // user_metadata e gravavel pelo proprio usuario: nunca usar como papel.
      const r = u?.app_metadata?.role || null;
      return r ? String(r) : null;
    } catch {
      return null;
    }
  })();
  // In strict impersonation mode, never allow admin fallback from JWT metadata.
  const isAdmin = isImpersonatingStrict
    ? isAdminRole(profileRole, profileStatus)
    : (isAdminRole(profileRole, profileStatus) || String(legacyRole || '').toLowerCase() === 'admin');

  const value = useMemo(() => ({
    session,
    profile,
    profileRole,
    profileStatus,
    isAuthenticated,
    isAdmin,
    isImpersonatingStrict,
    bootLoading,
    authzLoading,
    permissionsJson: profile?.permissions || {},
    hasPermission: (permission) => hasPermissionImpl({ role: isAdmin ? 'Admin' : profileRole, permissionSet }, permission),
    refreshAuthorization: async () => {
      clearAuthorizationCache();
      if (!session?.user?.id) return null;
      setAuthzLoading(true);
      try {
        const res = await fetchMyAuthorization(supabase, { ttlMs: 60_000 });
        setProfile(res.profile || null);
        setProfileRole(res.role || null);
        setProfileStatus(res.status || null);
        setPermissionSet(buildPermissionSet({ role: res.role, permissionsJson: res.permissionsJson }));
        return res;
      } finally {
        setAuthzLoading(false);
      }
    },
    login: async ({ email, password }) => {
      const res = await loginWithPassword(supabase, { email, password });
      try {
        await fetchMyAuthorization(supabase, { ttlMs: 60_000 });
      } catch {}
      return res;
    },
    logout: async () => {
      clearAuthorizationCache();
      setProfile(null);
      setProfileRole(null);
      setProfileStatus(null);
      setPermissionSet({ allow: new Set(), deny: new Set() });
      await logout(supabase);
    },
    requestPasswordReset: async ({ email, redirectTo } = {}) => {
      // Usa o cliente "implicit" para que o link do e-mail funcione em qualquer
      // navegador/dispositivo (o fluxo PKCE exige o mesmo navegador).
      return requestPasswordReset(supabasePasswordReset, { email, redirectTo });
    },
  }), [session, profile, profileRole, profileStatus, isAuthenticated, isAdmin, isImpersonatingStrict, bootLoading, authzLoading, permissionSet]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>.');
  return ctx;
}
